import { useEffect, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import { isComplimentaryEnhancedMonitoringUser } from "../../admin/PropertyUsers/userDisplayHelpers";
import { isAffiliateUser } from "../../helpers/affiliateSignup";
import { isEldoradoFromWhere } from "../../helpers/eldoradoSignup";

export const ENHANCED_MONITORING_COMPLIMENTARY =
  "enhanced_monitoring_complimentary";

export const CATEGORY_LABELS = {
  basic_monitoring: "Basic Monitoring",
  enhanced_monitoring: "Enhanced Monitoring",
  [ENHANCED_MONITORING_COMPLIMENTARY]: "Enhanced Monitoring (Complimentary)",
  complete_protection: "Complete Protection",
};

const adminDialogPaperSx = {
  borderRadius: 3,
  overflow: "hidden",
  maxWidth: 440,
  width: "100%",
  m: { xs: 2, sm: 3 },
  boxShadow:
    "0 24px 48px rgba(13, 18, 11, 0.12), 0 0 1px rgba(13, 18, 11, 0.08)",
};

const adminPrimaryButtonSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  py: 1.35,
  fontSize: "1rem",
  bgcolor: "#15be53",
  boxShadow: "0 4px 16px rgba(21, 190, 83, 0.35)",
  "&:hover": {
    bgcolor: "#12a649",
    boxShadow: "0 6px 20px rgba(21, 190, 83, 0.4)",
  },
};

const adminOutlinedButtonSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  py: 1.1,
  borderColor: "rgba(13, 18, 11, 0.18)",
  color: "#0D120B",
  "&:hover": {
    borderColor: "#0D120B",
    bgcolor: "rgba(13, 18, 11, 0.04)",
  },
};

/**
 * True when admin is adding flow for a user whose primary role is Customer.
 */
export function shouldPromptAdminCustomerCategoryConfirm(
  isAdmin,
  selectedRowData,
) {
  const primaryRole = Array.isArray(selectedRowData?.role)
    ? selectedRowData.role[0]
    : selectedRowData?.role;
  return (
    isAdmin &&
    selectedRowData?.category !== "complete_protection" &&
    String(primaryRole || "").toLowerCase() === "customer" &&
    !isComplimentaryEnhancedMonitoringUser(selectedRowData) &&
    !isEldoradoFromWhere(selectedRowData) &&
    !isAffiliateUser(selectedRowData)
  );
}

/**
 * Two-step dialog: confirm row/signup category, or pick a different plan category.
 */
const AdminCustomerCategoryModal = ({
  open,
  onClose,
  /** Category shown on confirm step (row data or signup fallback) */
  resolvedCategoryForConfirm,
  /** Called with the category key to use when user confirms either step */
  onContinue,
  /** Super Admin / Internal Admin: offer complimentary Enhanced in the picker */
  showComplimentaryEnhancedOption = false,
}) => {
  const [step, setStep] = useState("confirm");
  const [pickedCategory, setPickedCategory] = useState("");

  useEffect(() => {
    if (!open) {
      setStep("confirm");
      setPickedCategory("");
    }
  }, [open]);

  const displayPlanName =
    CATEGORY_LABELS[resolvedCategoryForConfirm] || resolvedCategoryForConfirm;

  const displayPlanKey = useMemo(() => {
    if (CATEGORY_LABELS[resolvedCategoryForConfirm]) {
      return resolvedCategoryForConfirm;
    }
    const normalizedPlanName = String(displayPlanName || "")
      .toLowerCase()
      .trim();
    return (
      Object.keys(CATEGORY_LABELS).find(
        (key) => CATEGORY_LABELS[key].toLowerCase() === normalizedPlanName,
      ) || ""
    );
  }, [displayPlanName, resolvedCategoryForConfirm]);

  const selectableCategories = useMemo(() => {
    let categories;
    if (displayPlanKey === ENHANCED_MONITORING_COMPLIMENTARY) {
      // Complimentary default (county investor): paid Enhanced or Complete Protection.
      categories = ["enhanced_monitoring", "complete_protection"];
    } else if (displayPlanKey === "enhanced_monitoring") {
      categories = ["complete_protection"];
    } else if (displayPlanKey === "basic_monitoring") {
      categories = ["enhanced_monitoring", "complete_protection"];
    } else {
      categories = [
        "basic_monitoring",
        "enhanced_monitoring",
        "complete_protection",
      ];
    }

    if (!showComplimentaryEnhancedOption) {
      return categories;
    }

    const enhancedIndex = categories.indexOf("enhanced_monitoring");
    if (enhancedIndex >= 0) {
      return [
        ...categories.slice(0, enhancedIndex + 1),
        ENHANCED_MONITORING_COMPLIMENTARY,
        ...categories.slice(enhancedIndex + 1),
      ];
    }
    return [ENHANCED_MONITORING_COMPLIMENTARY, ...categories];
  }, [displayPlanKey, showComplimentaryEnhancedOption]);

  const handleConfirmYes = () => {
    onContinue(resolvedCategoryForConfirm);
  };

  const handleChooseDifferent = () => {
    setPickedCategory(
      selectableCategories.includes(resolvedCategoryForConfirm)
        ? resolvedCategoryForConfirm
        : selectableCategories[0] || "complete_protection",
    );
    setStep("select");
  };

  const handleSelectContinue = () => {
    if (!pickedCategory) return;
    onContinue(pickedCategory);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      scroll="body"
      aria-labelledby={
        step === "confirm"
          ? "admin-category-dialog-title"
          : "admin-category-select-title"
      }
      PaperProps={{ sx: adminDialogPaperSx }}
    >
      {step === "confirm" ? (
        <>
          <DialogTitle
            id="admin-category-dialog-title"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              py: 2,
              px: 2.5,
              pr: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: "rgba(21, 190, 83, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VerifiedUserIcon sx={{ color: "#15be53", fontSize: 26 }} />
              </Box>
              <Box>
                <Typography
                  component="span"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.125rem",
                    lineHeight: 1.3,
                  }}
                >
                  Confirm category
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.25 }}
                >
                  Admin review — customer account
                </Typography>
              </Box>
            </Stack>
            <IconButton
              aria-label="Close dialog"
              onClick={onClose}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, lineHeight: 1.6 }}
            >
              You are about to continue signup using the plan category linked to
              this customer. Confirm if it is correct, or choose a different
              category.
            </Typography>
            <Box
              sx={{
                bgcolor: "rgba(21, 190, 83, 0.08)",
                border: "1px solid rgba(21, 190, 83, 0.28)",
                borderRadius: 2,
                px: 2.5,
                py: 2.25,
                textAlign: "center",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mb: 0.75,
                  letterSpacing: "0.06em",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "0.7rem",
                }}
              >
                Selected plan
              </Typography>
              <Typography
                variant="h6"
                component="p"
                sx={{
                  fontWeight: 700,
                  color: "#0D120B",
                  m: 0,
                  fontSize: "1.15rem",
                }}
              >
                {displayPlanName}
              </Typography>
            </Box>
          </DialogContent>
          <Divider />
          <DialogActions
            sx={{
              flexDirection: "column",
              alignItems: "stretch",
              gap: 1.25,
              px: 2.5,
              py: 2.5,
              pt: 2,
            }}
          >
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleConfirmYes}
              sx={adminPrimaryButtonSx}
            >
              Yes, continue with this plan
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleChooseDifferent}
              sx={adminOutlinedButtonSx}
            >
              Choose a different category
            </Button>
            <Button
              fullWidth
              onClick={onClose}
              sx={{
                textTransform: "none",
                color: "text.secondary",
                py: 0.5,
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle
            id="admin-category-select-title"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              py: 2,
              px: 2.5,
              pr: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: "rgba(3, 149, 255, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TuneOutlinedIcon sx={{ color: "#0395ff", fontSize: 26 }} />
              </Box>
              <Box>
                <Typography
                  component="span"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.125rem",
                    lineHeight: 1.3,
                  }}
                >
                  Select category
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.25 }}
                >
                  Pick the plan category for this customer
                </Typography>
              </Box>
            </Stack>
            <IconButton
              aria-label="Close dialog"
              onClick={onClose}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, lineHeight: 1.6 }}
            >
              Select which protection category this signup should use. You can
              go back to the previous step if you need to double-check.
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="admin-owner-verification-category-label">
                Plan category
              </InputLabel>
              <Select
                labelId="admin-owner-verification-category-label"
                id="admin-owner-verification-category"
                value={pickedCategory}
                label="Plan category"
                onChange={(e) => setPickedCategory(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {selectableCategories.map((categoryKey) => (
                  <MenuItem key={categoryKey} value={categoryKey}>
                    {CATEGORY_LABELS[categoryKey] || categoryKey}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <Divider />
          <DialogActions
            sx={{
              flexDirection: "column",
              alignItems: "stretch",
              gap: 1.25,
              px: 2.5,
              py: 2.5,
              pt: 2,
            }}
          >
            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={!pickedCategory}
              onClick={handleSelectContinue}
              sx={adminPrimaryButtonSx}
            >
              Continue with selected plan
            </Button>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
            >
              <Button
                onClick={() => setStep("confirm")}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#0395ff",
                }}
              >
                ← Back
              </Button>
              <Button
                onClick={onClose}
                sx={{ textTransform: "none", color: "text.secondary" }}
              >
                Cancel
              </Button>
            </Stack>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

AdminCustomerCategoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  resolvedCategoryForConfirm: PropTypes.string.isRequired,
  onContinue: PropTypes.func.isRequired,
  showComplimentaryEnhancedOption: PropTypes.bool,
};

export default AdminCustomerCategoryModal;
